import React, { useState, useEffect } from 'react';
import { AlertCircle, X, Wrench, Clock } from 'lucide-react';

const MaintenanceNotice: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenShown, setHasBeenShown] = useState(false);

  useEffect(() => {
    // 한국시간 2025년 10월 18일 23:59:59 (UTC+9) - 오늘 하루 종일 표시
    const endDate = new Date('2025-10-18T23:59:59+09:00');
    const now = new Date();

    // 세션 스토리지에서 확인 (브라우저 닫기 전까지만 유지)
    const sessionKey = 'maintenance-notice-shown-session';
    const wasShownInSession = sessionStorage.getItem(sessionKey);

    // 종료 시간 전이고, 이번 세션에서 아직 안 봤으면 즉시 표시
    if (now < endDate && !wasShownInSession) {
      // 즉시 표시 (딜레이 없음)
      setIsVisible(true);
      setHasBeenShown(true);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    // 세션 스토리지에 저장 (브라우저 닫으면 다시 표시)
    const sessionKey = 'maintenance-notice-shown-session';
    sessionStorage.setItem(sessionKey, 'true');
  };

  if (!hasBeenShown) return null;

  return (
    <>
      {/* 오버레이 */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleClose}
      />

      {/* 팝업 컨테이너 */}
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none transition-all duration-300 ${
          isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        <div
          className="bg-white rounded-3xl shadow-2xl max-w-md w-full pointer-events-auto transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* 헤더 - 그라데이션 배경 */}
          <div className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 rounded-t-3xl p-8 pb-12">
            {/* 닫기 버튼 */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
              aria-label="닫기"
            >
              <X className="w-6 h-6" />
            </button>

            {/* 아이콘 */}
            <div className="flex justify-center mb-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                <Wrench className="w-12 h-12 text-white" strokeWidth={2.5} />
              </div>
            </div>

            {/* 제목 */}
            <h2 className="text-2xl font-bold text-white text-center mb-2">
              🔧 서비스 점검 중
            </h2>
            <p className="text-white/90 text-center text-sm">
              시스템 개선 작업이 진행되고 있습니다
            </p>
          </div>

          {/* 컨텐츠 */}
          <div className="p-8 pt-6">
            {/* 안내 메시지 */}
            <div className="bg-gradient-to-br from-rose-50 to-orange-50 rounded-2xl p-6 mb-6 border-2 border-rose-200">
              <div className="flex items-start gap-3 mb-4">
                <AlertCircle className="w-6 h-6 text-rose-600 flex-shrink-0 mt-0.5 animate-pulse" />
                <div className="flex-1">
                  <p className="text-slate-800 font-bold mb-3 text-base">
                    ⚠️ 현재 서비스 점검 중입니다
                  </p>
                  <p className="text-slate-700 text-sm leading-relaxed mb-2">
                    더 나은 서비스 제공을 위해 시스템 개선 작업을 진행하고 있습니다.
                  </p>
                  <p className="text-rose-700 text-sm font-semibold bg-rose-100 px-3 py-2 rounded-lg">
                    💡 로그인/회원가입 기능이 일시적으로 제한될 수 있습니다
                  </p>
                </div>
              </div>

              {/* 작업 완료 시간 */}
              <div className="border-t border-rose-200/50 pt-4 mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-rose-600" />
                  <span className="text-slate-700 font-semibold text-sm">작업 완료 예정</span>
                </div>
                <p className="text-rose-700 font-bold text-lg">
                  2025년 10월 18일 오후 11시 59분
                </p>
                <p className="text-slate-600 text-xs mt-1">
                  * 상황에 따라 조기 완료될 수 있습니다
                </p>
              </div>
            </div>

            {/* 작업 내용 */}
            <div className="space-y-3 mb-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">
                📋 진행 중인 작업
              </h3>
              <div className="space-y-2">
                {[
                  '✅ 크루 시스템 재구축 완료',
                  '🔄 데이터베이스 스키마 업데이트 중',
                  '🔄 인증 시스템 최적화 진행 중',
                  '⏳ 전체 시스템 안정화 작업 중',
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 mt-2 flex-shrink-0" />
                    <p className="text-slate-600 text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* 추가 안내 */}
            <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-200">
              <p className="text-blue-800 text-sm font-medium mb-2">
                🙏 이용에 불편을 드려 죄송합니다
              </p>
              <p className="text-blue-700 text-xs leading-relaxed">
                작업 완료 후 더욱 안정적인 서비스로 찾아뵙겠습니다. 
                감사합니다.
              </p>
            </div>

            {/* 확인 버튼 */}
            <button
              onClick={handleClose}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-4 rounded-2xl transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl"
            >
              확인했습니다
            </button>

            {/* 푸터 텍스트 */}
            <p className="text-center text-slate-400 text-xs mt-4">
              불편을 드려 죄송합니다. 더 나은 서비스로 찾아뵙겠습니다. 💙
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default MaintenanceNotice;

